import {
  ACCESS_DENIED_MESSAGE,
  CHAT_HISTORY,
  type Ack,
  type ChatMessage,
  type ClientToServerEvents,
  type GameSettings,
  type LatLng,
  type PartyOptions,
  type PartyReaction,
  type PartySettings,
  type PartySnapshot,
  type PickPreview,
  type PlayerProfile,
  type Reaction,
  type TeamIndex,
  type ServerToClientEvents,
} from '@okwhere/shared';
import type { Socket } from 'socket.io-client';
import { create } from 'zustand';
import { accessToken, playerToken, playerTokenReady, setAccessToken } from '../../lib/storage';

type PartySocket = Socket<ServerToClientEvents, ClientToServerEvents>;
export type ConnectionState = 'idle' | 'connecting' | 'online' | 'offline';

/** How long a reaction floats on screen. */
export const REACTION_MS = 2600;
export interface FloatingReaction extends PartyReaction {
  id: number;
}
let reactionSeq = 0;

interface PartyState {
  connection: ConnectionState;
  snapshot: PartySnapshot | null;
  /** Server clock minus local clock, in ms. Add it to `Date.now()` to get server time. */
  clockOffset: number;
  /** Why we're no longer in a party, when it wasn't our choice. */
  exit: { code: string; reason: string } | null;
  /** Reactions currently floating on screen. */
  reactions: FloatingReaction[];
  /** The party's chat, oldest first. */
  chat: ChatMessage[];

  create(profile: PlayerProfile, settings: GameSettings, options: PartyOptions): Promise<string>;
  join(code: string, profile: PlayerProfile): Promise<void>;
  /** Follows a party as a spectator (TV screen): no seat, no panorama. */
  watch(code: string): Promise<void>;
  leave(): void;
  updateProfile(profile: PlayerProfile): Promise<void>;
  updateSettings(patch: Partial<PartySettings>): Promise<void>;
  setTeam(team: TeamIndex): Promise<void>;
  renameTeam(team: TeamIndex, name: string): Promise<void>;
  /** Takes our locked guess back (when the party allows it). */
  unguess(roundIndex: number): Promise<void>;
  shuffleTeams(): Promise<void>;
  start(): Promise<void>;
  guess(roundIndex: number, point: LatLng): Promise<void>;
  /** Sends an emoji to everyone in the party (fire and forget). */
  react(emoji: Reaction): void;
  /** Posts a chat message to the party. */
  say(text: string): Promise<void>;
  /** Tells the server where our pin is (fire and forget). */
  pin(roundIndex: number, point: LatLng): void;
  endRound(): Promise<void>;
  /** Take turns: finds the Street View nearest to a spot we clicked, during our turn. */
  previewPick(roundIndex: number, point: LatLng): Promise<PickPreview>;
  /** Take turns: plays the place we looked at last, or a random one (we guess too, then). */
  choosePick(roundIndex: number, random: boolean): Promise<void>;
  next(): Promise<void>;
  rematch(): Promise<void>;
  kick(playerId: string): Promise<void>;
}

const ACK_TIMEOUT_MS = 10_000;

let socket: PartySocket | null = null;
/** The party we're seated in, to rejoin automatically after a reconnection. */
let session: { code: string; profile: PlayerProfile } | null = null;
/** The party this tab watches as a spectator (TV screen), to watch again after a reconnection. */
let watching: string | null = null;
/**
 * Which party's snapshots to accept: a room code, any room while creating one ('*'), or none
 * after leaving, so a snapshot still in flight can't put us back in a party we just left.
 */
let accepting: string | null = null;

/** The party a spectator watched is gone: show why instead of its last screen. */
function stopWatching(code: string, reason: string): void {
  watching = null;
  accepting = null;
  usePartyStore.setState({ connection: 'online', snapshot: null, exit: { code, reason } });
}

/** Socket answers carry no status: a refused token is recognized by its (shared) message. */
function forgetDeniedToken(error: unknown): void {
  if (error instanceof Error && error.message === ACCESS_DENIED_MESSAGE) setAccessToken(null);
}

function unwrap<T>(response: Ack<T>): T {
  if (!response.ok) throw new Error(response.error);
  return response.data;
}

async function send<T>(emit: (socket: PartySocket) => Promise<Ack<T>>): Promise<T> {
  const s = await connectedSocket();
  let response: Ack<T>;
  try {
    response = await emit(s);
  } catch {
    throw new Error('The server is not responding. Check your connection and try again.');
  }
  return unwrap(response);
}

async function syncClock(s: PartySocket): Promise<void> {
  let best: { rtt: number; offset: number } | null = null;
  for (let i = 0; i < 4; i++) {
    const sentAt = Date.now();
    try {
      const serverTime = await s.timeout(5000).emitWithAck('time:sync');
      const receivedAt = Date.now();
      const sample = { rtt: receivedAt - sentAt, offset: serverTime - (sentAt + receivedAt) / 2 };
      if (!best || sample.rtt < best.rtt) best = sample;
    } catch {
      return;
    }
  }
  if (best) usePartyStore.setState({ clockOffset: best.offset });
}

/** Loads the socket.io client the first time it's needed, so the home page doesn't carry it. */
let socketReady: Promise<PartySocket> | null = null;

async function connectedSocket(): Promise<PartySocket> {
  socketReady ??= import('socket.io-client').then(({ io }) => {
    const s: PartySocket = io({ autoConnect: false, transports: ['websocket', 'polling'], reconnectionDelayMax: 4000 });
    socket = s;

    /** Takes our seat back after a reconnection; we only count as online once that worked. */
    const rejoin = () => {
      if (!session || !s.connected) return;
      const { code, profile } = session;
      s.timeout(ACK_TIMEOUT_MS)
        .emitWithAck('party:join', { token: playerToken(), code, profile, reloaded: false })
        .then((response) => {
          if (response.ok) {
            usePartyStore.setState({ connection: 'online' });
          } else {
            session = null;
            accepting = null;
            usePartyStore.setState({ connection: 'online', snapshot: null, exit: { code, reason: response.error } });
          }
        })
        .catch(() => {
          // No answer in time: try again while the socket is still up.
          window.setTimeout(rejoin, 2000);
        });
    };

    /** Watches again after a reconnection; the party may have ended meanwhile (a deploy, say). */
    const rewatch = () => {
      const code = watching;
      if (!code || !s.connected) return;
      s.timeout(ACK_TIMEOUT_MS)
        .emitWithAck('party:watch', { code })
        .then((response) => {
          if (response.ok) usePartyStore.setState({ connection: 'online' });
          else stopWatching(code, response.error);
        })
        .catch(() => window.setTimeout(rewatch, 2000));
    };

    s.on('connect', () => {
      void syncClock(s);
      if (session) rejoin();
      else if (watching) rewatch();
      else usePartyStore.setState({ connection: 'online' });
    });
    s.on('disconnect', () => usePartyStore.setState({ connection: 'offline' }));
    s.io.on('reconnect_attempt', () => usePartyStore.setState({ connection: 'connecting' }));
    s.on('party:snapshot', (snapshot) => {
      if (accepting === null || (accepting !== '*' && snapshot.code !== accepting)) return;
      usePartyStore.setState({ snapshot, exit: null });
    });
    s.on('party:reaction', (reaction) => {
      if (accepting === null) return;
      const id = ++reactionSeq;
      usePartyStore.setState((state) => ({ reactions: [...state.reactions, { ...reaction, id }] }));
      window.setTimeout(
        () => usePartyStore.setState((state) => ({ reactions: state.reactions.filter((shown) => shown.id !== id) })),
        REACTION_MS,
      );
    });
    s.on('party:chat-history', (messages) => {
      if (accepting !== null) usePartyStore.setState({ chat: messages });
    });
    s.on('party:chat', (message) => {
      if (accepting === null) return;
      usePartyStore.setState((state) => ({ chat: [...state.chat, message].slice(-CHAT_HISTORY) }));
    });
    s.on('party:closed', () => {
      if (watching) stopWatching(watching, 'This party has ended.');
    });
    s.on('party:kicked', () => {
      const code = session?.code ?? usePartyStore.getState().snapshot?.code ?? '';
      session = null;
      accepting = null;
      usePartyStore.setState({ snapshot: null, exit: { code, reason: 'The host removed you from the party.' } });
    });
    return s;
  });
  const s = await socketReady;
  if (s.disconnected) {
    usePartyStore.setState({ connection: 'connecting' });
    s.connect();
  }
  return s;
}

export const usePartyStore = create<PartyState>()(() => ({
  connection: 'idle',
  snapshot: null,
  clockOffset: 0,
  exit: null,
  reactions: [],
  chat: [],

  async create(profile, settings, options) {
    await playerTokenReady;
    usePartyStore.setState({ chat: [] });
    accepting = '*';
    try {
      const { code } = await send<{ code: string }>((s) =>
        s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:create', {
          token: playerToken(),
          profile,
          settings,
          options,
          accessToken: accessToken() ?? undefined,
        }),
      );
      session = { code, profile };
      accepting = code;
      return code;
    } catch (error) {
      accepting = session?.code ?? null;
      forgetDeniedToken(error);
      throw error;
    }
  },

  async join(code, profile) {
    await playerTokenReady;
    usePartyStore.setState({ chat: [] });
    usePartyStore.setState({ exit: null });
    accepting = code.toUpperCase();
    try {
      await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:join', { token: playerToken(), code, profile }));
    } catch (error) {
      // The server keeps us in our previous party when a join fails.
      accepting = session?.code ?? null;
      throw error;
    }
    session = { code: code.toUpperCase(), profile };
  },

  async watch(code) {
    session = null;
    watching = code.toUpperCase();
    accepting = watching;
    usePartyStore.setState({ exit: null, chat: [] });
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:watch', { code }));
  },

  leave() {
    session = null;
    watching = null;
    accepting = null;
    socket?.emit('party:leave');
    usePartyStore.setState({ snapshot: null, exit: null, chat: [] });
  },

  async updateProfile(profile) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:profile', profile));
    if (session) session = { ...session, profile };
  },

  async updateSettings(patch) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:settings', patch));
  },

  async setTeam(team) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:team', { team }));
  },

  async renameTeam(team, name) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:team-name', { team, name }));
  },

  async unguess(roundIndex) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:unguess', { roundIndex }));
  },

  async shuffleTeams() {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:shuffle-teams'));
  },

  async start() {
    // Finding locations can take a few seconds on a cold server.
    try {
      await send((s) => s.timeout(45_000).emitWithAck('party:start', { accessToken: accessToken() ?? undefined }));
    } catch (error) {
      forgetDeniedToken(error);
      throw error;
    }
  },

  async guess(roundIndex, point) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:guess', { roundIndex, guess: point }));
  },

  async say(text) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:chat', { text }));
  },

  react(emoji) {
    socket?.emit('party:react', { emoji });
  },

  pin(roundIndex, point) {
    socket?.emit('party:pin', { roundIndex, guess: point });
  },

  async endRound() {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:end-round'));
  },

  async previewPick(roundIndex, point) {
    return send((s) => s.timeout(15_000).emitWithAck('party:pick-preview', { roundIndex, point }));
  },

  async choosePick(roundIndex, random) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:pick', { roundIndex, random }));
  },

  async next() {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:next'));
  },

  async rematch() {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:rematch'));
  },

  async kick(playerId) {
    await send((s) => s.timeout(ACK_TIMEOUT_MS).emitWithAck('party:kick', { playerId }));
  },
}));

/** Converts a server timestamp to the local clock. */
export function useLocalTime(): (serverTime: number) => number {
  const offset = usePartyStore((state) => state.clockOffset);
  return (serverTime: number) => serverTime - offset;
}
